import React from 'react';
import styled from 'styled-components';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { Paper, Box } from '@material-ui/core';
import ImageIcon from '@material-ui/icons/Image';

import { CardDb } from '../redux/cardDbSlice';
import { StaticImageUiSketchItem, UiCardItemDb, isStaticImageUiSketchItem } from '../redux/uiCardItemDbSlice';
import { FontType } from '../redux/cardAttributeModelIndex';
import { deepCopy } from '../general';
import { initial } from '../card_components/index';
import useImageData from '../hooks/useImageData';
import { EditUiCardItemDbHashType } from './EditDialog';

type CardPaperProps = {
  width: number;
  height: number;
  frameDisplay: boolean;
  frameColor: string;
  scaleValue: number;
}
const CardPaper = styled(Paper)<CardPaperProps>`
  position: absolute;
  align-self: center;
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
  border: ${props => props.frameDisplay ? '1px solid ' + props.frameColor : undefined};
  overflow: hidden;
  transform: ${props => 'scale(' + props.scaleValue + ')'};
  transform-origin: left top;
`
type UiPartsDivProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  selecting: boolean;
}
const UiPartsDiv = styled.div<UiPartsDivProps>`
  position: absolute;
  left: 0;
  top: 0;
  overflow: visible;
  white-space: nowrap;
  border: ${props => props.selecting ? '1px blue solid' : undefined};
  box-sizing: border-box;
  transform: ${props => 'translate(' + props.x + 'px, ' + props.y + 'px) !important'};
  transform-origin: center;
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
`
type UiPartsInputDivProps = UiPartsDivProps & {
  labelVisibility: boolean;
  size: number;
};
const UiPartsInputDiv = styled.div<UiPartsInputDivProps>`
  position: absolute;
  display: grid;
  grid-template-rows: ${props => props.labelVisibility ? (props.size + props.size / 2) + 'px auto' : undefined};
  left: 0;
  top: 0;
  overflow: visible;
  border: ${props => props.selecting ? '1px blue solid' : undefined};
  box-sizing: border-box;
  transform: ${props => 'translate(' + props.x + 'px, ' + props.y + 'px) !important'};
  transform-origin: center;
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
`
const UiPartsCheckboxDiv = styled.div<UiPartsDivProps>`
  position: absolute;
  left: 0;
  top: 0;
  overflow: visible;
  display: flexbox;
  align-items: center;
  border: ${props => props.selecting ? '1px blue solid' : undefined};
  box-sizing: border-box;
  transform: ${props => 'translate(' + props.x + 'px, ' + props.y + 'px) !important'};
  transform-origin: center;
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
`
const UiPartsVariableImageDiv = styled.div<UiPartsDivProps>`
  position: absolute;
  left: 0;
  top: 0;
  overflow: hidden;
  overflow-wrap: anywhere;
  border: ${props => props.selecting ? '1px blue solid' : undefined};
  box-sizing: border-box;
  transform: ${props => 'translate(' + props.x + 'px, ' + props.y + 'px) !important'};
  transform-origin: center;
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
`
const UiPartsStaticImageDiv = styled.div<UiPartsDivProps>`
  position: absolute;
  left: 0;
  top: 0;
  border: ${props => props.selecting ? '1px blue solid' : undefined};
  box-sizing: border-box;
  transform: ${props => {
    if (props.width < 0) return 'translate(' + (props.x + props.width) + 'px, ' + props.y + 'px) scaleX(-1) !important';
    return 'translate(' + props.x + 'px, ' + props.y + 'px) !important';
  }};
  transform-origin: center;
  width: ${props => Math.abs(props.width) + 'px'};
  height: ${props => props.height + 'px'};
`
type TextDivProps = {
  flame: boolean;
  flameColor: string;
  backgroundOpacity: number;
  backgroundColor: string;
} & {[P in keyof FontType]?: FontType[P]};
const TextDiv = styled.div<TextDivProps>`
  display: grid;
  overflow-wrap: anywhere;
  width: inherit;
  overflow: hidden;
  border: ${props => props.flame ? 'solid 4px ' + props.flameColor : undefined};
  border-radius: 10px 50px/50px 10px;
  box-sizing: border-box;
  background-color: ${props => {
    const hexArray = props.backgroundColor.split('');
    const r = parseInt(hexArray[1] + hexArray[2], 16);
    const g = parseInt(hexArray[3] + hexArray[4], 16);
    const b = parseInt(hexArray[5] + hexArray[6], 16);
    return 'rgba(' + r + ',' + g + ',' + b + ',' + props.backgroundOpacity + ')'
  }};
  font-size: ${props => props.size + 'px'};
  font-weight: ${props => props.bold ? 'bold' : 'normal'};
  color: ${props => props.color};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  text-decoration: ${props => props.underline ? 'underline' : undefined};
  font-family: ${props => props.name};
  text-align: ${props => props.yoko};
  align-items: ${props => props.tate};
  writing-mode: ${props => props.direction};
  text-orientation: ${props => props.orientation};
`
type UiPartsLabelProps = {[P in keyof FontType]?: FontType[P]};
const UiPartsLabel = styled.label<UiPartsLabelProps>`
  white-space: nowrap;
  overflow: visible;
  font-size: ${props => props.size + 'px'};
  font-weight: ${props => props.bold ? 'bold' : 'normal'};
  color: ${props => props.color};
  font-style: 'normal';
  text-decoration: ${props => props.underline ? 'underline' : undefined};
  font-family: ${props => props.name};
  pointer-events: none;
  text-align: 'left';
  align-items: 'normal';
  writing-mode: 'horizontal-tb';
`
const UiPartsInputText = styled.input`
  width: inherit;
  padding: .5em 1em;
  border: solid 4px #000000;
  border-radius: 10px 50px/50px 10px;
  box-sizing: border-box;
  pointer-events: none;
`
type UiPartsButtonProps = {
  backgroundColor: string
} & {[P in keyof FontType]?: FontType[P]};
const UiPartsButton = styled.button<UiPartsButtonProps>`
  width: inherit;
  height: inherit;
  border: solid 4px #000000;
  border-radius: 10px 50px/50px 10px;
  box-sizing: border-box;
  background-color: ${props => props.backgroundColor};
  font-size: ${props => props.size + 'px'};
  font-weight: ${props => props.bold ? 'bold' : 'normal'};
  color: ${props => props.color};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  font-family: ${props => props.name};
`
type CheckboxUiPartsProps = {
  size: number;
}
const CheckboxUiParts = styled.div<CheckboxUiPartsProps>`
  box-sizing: border-box;
  width: ${props => props.size + 'px'};
  height: ${props => props.size + 'px'};
  margin-right: 4px;
  border: solid 4px #000000;
  border-radius: 10px 50px/50px 10px;
`
const CheckboxCheckUiParts = styled.div<CheckboxUiPartsProps>`
  position: absolute;
  display: block;
  box-sizing: border-box;
  width: ${props => props.size * 0.9 + 'px'};
  height: ${props => props.size / 2 + 'px'};
  top: 12%;
  left: ${props => props.size / 20 + 'px'};
  transform: rotate(-45deg);
  transform-origin: center;
  border-bottom: 4px solid;
  border-left: 4px solid;
  border-color: #000000;
`
const SelectUiParts = styled.select`
  width: inherit;
  padding: .5em 1em;
  border: solid 4px #000000;
  border-radius: 10px 50px/50px 10px;
  box-sizing: border-box;
  pointer-events: none;
`
const MultiUiParts = styled.textarea`
  width: inherit;
  padding: .5em 1em;
  border: solid 4px #000000;
  border-radius: 10px 50px/50px 10px;
  box-sizing: border-box;
  pointer-events: none;
`
type UiPartsImageIconProps = {
  size: number;
}
const UiPartsImageIcon = styled(ImageIcon)<UiPartsImageIconProps>`
  position: absolute;
  left: 50%;
  top: 50%;
  color: #e4e4e4;
  font-size: ${props => (props.size / 2) + 'px'};
  z-index: -1;
  transform: translate(-50%, -50%);
`
type ImageUiPartsProps = {
  radius: number;
}
const VariableImageUiParts = styled.div<ImageUiPartsProps>`
  width: inherit;
  height: inherit;
  overflow: hidden;
  overflow-wrap: anywhere;
  border: solid 4px #000000;
  border-radius: ${props => (props.radius / 2) + '%'};
  box-sizing: border-box;
  display: grid;
  align-items: center;
  justify-items: center;
`
const StaticImageUiParts = styled.img<ImageUiPartsProps>`
  width: inherit;
  height: inherit;
  border-radius: ${props => (props.radius / 2) + '%'};
  box-sizing: border-box;
`
const GroupDiv = styled.div`
  overflow: hidden;
  width: inherit;
  height: inherit;
  border: solid 4px black;
  border-radius: 10px 50px/50px 10px;
  box-sizing: border-box;
`
type GroupUiPartsProps = {
  width: number;
  height: number;
}
const GroupUiParts = styled.div<GroupUiPartsProps>`
  position: absolute;
  overflow: hidden;
  width: ${props => props.width + 'px'};
  height: ${props => props.height + 'px'};
  border-radius: 10px 50px/50px 10px;
  background-color: #ffffff;
`
type ResizeBoxSvgProps = {
  x: number;
  y: number;
  rotation: number;
  imageWidth?: number;
}
const ResizeBoxSvg = styled.svg<ResizeBoxSvgProps>`
  position: absolute;
  z-index: 2;
  pointer-events: none;
  overflow: visible;
  transform-origin: center;
  transform: ${props => {
    if (typeof props.imageWidth === 'number' && props.imageWidth < 0) return 'translate(' + (props.x + props.imageWidth) + 'px, ' + props.y + 'px) rotate(' + props.rotation + 'deg)';
    else return 'translate(' + props.x + 'px, ' + props.y + 'px) rotate(' + props.rotation + 'deg)';
  }};
`
type ResizeHandleProps = {
  translateValue: {x: number, y: number, scale: number};
}
const ResizeHandleRect = styled.rect<ResizeHandleProps>`
  position: absolute;
  transform: ${props => 'translate(' + props.translateValue.x + 'px, ' + props.translateValue.y + 'px) scale(' + props.translateValue.scale + ') !important'};
  pointer-events: painted;
`

type ResizeBoxProps = {
  id: string;
  item: UiCardItemDb;
  scaleValue: number;
  changeItem: (item: any, id?: string) => void;
}
const ResizeBox: React.FC<ResizeBoxProps> = ({id, item, scaleValue, changeItem}) => {
  const sin = (rotation: number) => {
    return rotation % 180 === 0 ? 0 : Math.sin(Math.PI * rotation / 180);
  }
  const cos = (rotation: number) => {
    return (rotation - 90) % 180 === 0 ? 0 : Math.cos(Math.PI * rotation / 180);
  }
  const getDragVerticalAngle = (x: number, y: number): number => {
    if (y === 0 && x === 0) return 0;
    if (y === 0 && x > 0) return 90;
    else if (y === 0 && x < 0) return 270;
    else if (x >= 0 && y > 0) return Math.atan(Math.abs(x) / Math.abs(y)) * 180 / Math.PI;
    else if (x >= 0 && y < 0) return 180 - Math.atan(Math.abs(x) / Math.abs(y)) * 180 / Math.PI;
    else if (x < 0 && y < 0) return Math.atan(Math.abs(x) / Math.abs(y)) * 180 / Math.PI + 180;
    else return 360 - Math.atan(Math.abs(x) / Math.abs(y)) * 180 / Math.PI;
  }
  const getDragHorizontalAngle = (x: number, y: number): number => {
    if (y === 0 && x === 0) return 0;
    if (x === 0 && y > 0) return 90;
    else if (x === 0 && y < 0) return 270;
    else if (y >= 0 && x > 0) return Math.atan(Math.abs(y) / Math.abs(x)) * 180 / Math.PI;
    else if (y >= 0 && x < 0) return 180 - Math.atan(Math.abs(y) / Math.abs(x)) * 180 / Math.PI;
    else if (y < 0 && x < 0) return Math.atan(Math.abs(y) / Math.abs(x)) * 180 / Math.PI + 180;
    else return 360 - Math.atan(Math.abs(y) / Math.abs(x)) * 180 / Math.PI;
  }
  const getHandlePosition = (item: UiCardItemDb, spot: string): {x: number, y: number} => {
    let handleXPosition = 0;
    let handleYPosition = 0;
    if (spot === 'bottom') {
      handleXPosition = -item.height / 2 * sin(0);
      handleYPosition = item.height / 2 * cos(0);
    } else if (spot === 'right') {
      handleXPosition = item.width / 2 * cos(0);
      handleYPosition = item.width / 2 * sin(0);
    } else if (spot === 'rightBottom') {
      handleXPosition = item.width / 2 * cos(0) - item.height / 2 * sin(0);
      handleYPosition = item.height / 2 * cos(0) + item.width / 2 * sin(0);
    } else if (spot === 'leftTop') {
      handleXPosition = -item.width / 2 * cos(0) + item.height / 2 * sin(0);
      handleYPosition = -item.height / 2 * cos(0) - item.width / 2 * sin(0);
    } else if (spot === 'rightTop') {
      handleXPosition = item.width / 2 * cos(0) + item.height / 2 * sin(0);
      handleYPosition = -item.height / 2 * cos(0) + item.width / 2 * sin(0);
    } else if (spot === 'top') {
      handleXPosition = item.height / 2 * sin(0);
      handleYPosition = -item.height / 2 * cos(0);
    } else if (spot === 'left') {
      handleXPosition = -item.width / 2 * cos(0);
      handleYPosition = -item.width / 2 * sin(0);
    }
    const x = item.x + item.width / 2 + handleXPosition;
    const y = item.y + item.height / 2 + handleYPosition;
    return { x, y };
  }
  const getCursor = (item: UiCardItemDb, spot: string): string => {
    const cursorIcons = ['ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize'];
    type ixsHashType = {[key in string]: number};
    const ixs: ixsHashType = {'right': 0, 'rightBottom': item.width > 0 ? 1 : 3, 'bottom': 2};
    const initialIx = ixs[spot];
    const increaseIx = (value: number): number => {
      return (initialIx + value) % 4;
    }
    return cursorIcons[increaseIx(Math.round(0 / 45))];
  }
  const resizeItemBottom = (data: DraggableData, item: UiCardItemDb, id: string) => {
    const origin = getHandlePosition(item, 'bottom');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const dragAngle = getDragVerticalAngle(-drag.x, drag.y);
    const dragValue = cos(dragAngle) === 0 ? Math.abs(drag.x / sin(dragAngle)) : Math.abs(drag.y / cos(dragAngle));
    let resize = dragValue * cos(0 - dragAngle);
    if (item.height + resize < 0) resize = -item.height;

    const newAttrs = {
      x: item.x - resize / 2 * sin(0),
      y: item.y + resize / 2 * cos(0) - resize / 2,
      width: item.width,
      height: item.height + resize
    };
    const newItem = {...item, ...newAttrs};
    changeItem(newItem, id);
  }
  const resizeItemRight = (data: DraggableData, item: UiCardItemDb, id: string) => {
    const origin = getHandlePosition(item, 'right');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const dragAngle = getDragHorizontalAngle(drag.x, drag.y);
    const dragValue = sin(dragAngle) === 0 ? Math.abs(drag.x / cos(dragAngle)) : Math.abs(drag.y / sin(dragAngle));
    let resize = dragValue * cos(0 - dragAngle);
    if (!isStaticImageUiSketchItem(item) && item.width + resize < 0) resize = -item.width;

    let newAttrs = {
      x: item.x + resize / 2 * cos(0) - resize / 2,
      y: item.y + resize / 2 * sin(0),
      width: item.width + resize,
      height: item.height
    };
    const newItem = {...item, ...newAttrs};
    changeItem(newItem, id);
  }
  const resizeItemRightBottom = (data: DraggableData, item: UiCardItemDb, id: string) => {
    const origin = getHandlePosition(item, 'rightBottom');
    const drop = { x: data.x, y: data.y };
    const drag = { x: drop.x - origin.x, y: drop.y - origin.y };
    const resize = { width: drag.x * cos(0) + drag.y * sin(0), height: drag.x * -sin(0) + drag.y * cos(0) }
    if (!isStaticImageUiSketchItem(item) && item.width + resize.width < 0) resize.width = -item.width;
    if (item.height + resize.height < 0) resize.height = -item.height;

    let newAttrs = {
      x: item.x + resize.width / 2 * cos(0) - resize.height / 2 * sin(0) - resize.width / 2,
      y: item.y + resize.width / 2 * sin(0) + resize.height / 2 * cos(0) - resize.height / 2,
      width: item.width + resize.width,
      height: item.height + resize.height
    };
    if (isStaticImageUiSketchItem(item) && item.width * (item.width + resize.width) <= 0) document.body.style.cursor = getCursor({...item, width: item.width + resize.width}, 'rightBottom');
    const newItem = {...item, ...newAttrs};
    changeItem(newItem, id);
  }

  return (
    <ResizeBoxSvg width={Math.abs(item.width)} height={item.height}
      x={item.x} y={item.y} rotation={0} imageWidth={isStaticImageUiSketchItem(item) ? item.width : undefined}
      xmlns="http://www.w3.org/2000/svg">
      <Draggable position={getHandlePosition(item, 'bottom')} scale={scaleValue}
        onStart={() => {document.body.style.cursor = getCursor(item, 'bottom')}}
        onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemBottom(data, item, id)}
        onStop={() => {document.body.style.cursor = 'auto'}}
      >
        <ResizeHandleRect width={4} height={4} fill="white" stroke="black" strokeWidth="1"
          cursor={getCursor(item, 'bottom')}
          translateValue={{x: item.width >= 0 ? item.width / 2 - 3 : Math.abs(item.width) / 2 - 3, y: item.height - 5, scale: 1 / scaleValue}} />
      </Draggable>
      <Draggable position={getHandlePosition(item, 'right')} scale={scaleValue}
        onStart={() => {document.body.style.cursor = getCursor(item, 'right')}}
        onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemRight(data, item, id)}
        onStop={() => {document.body.style.cursor = 'auto'}}
      >
        <ResizeHandleRect width={4} height={4} fill="white" stroke="black" strokeWidth="1"
          cursor={getCursor(item, 'right')}
          translateValue={{x: item.width >= 0 ? item.width - 5 : -3, y: item.height / 2 - 3, scale: 1 / scaleValue}} />
      </Draggable>
      <Draggable position={getHandlePosition(item, 'rightBottom')} scale={scaleValue}
        onStart={() => {document.body.style.cursor = getCursor(item, 'rightBottom')}}
        onDrag={(ev: DraggableEvent, data: DraggableData) => resizeItemRightBottom(data, item, id)}
        onStop={() => {document.body.style.cursor = 'auto'}}
      >
        <ResizeHandleRect width={4} height={4} fill="white" stroke="black" strokeWidth="1"
          cursor={getCursor(item, 'rightBottom')}
          translateValue={{x: item.width >= 0 ? item.width - 5 : -3, y: item.height - 5, scale: 1 / scaleValue}} />
      </Draggable>
    </ResizeBoxSvg>
  );
}

type RenderImageProps = {
  item: StaticImageUiSketchItem;
  scaleValue: number;
  id: string;
  selectingId: string | undefined;
  setSelectingId: (id: string) => void;
  dragItem: (data: DraggableData, item: StaticImageUiSketchItem, id: string) => void;
  dragDisabled: boolean;
}
const RenderImage: React.FC<RenderImageProps> = ({item, scaleValue, id, selectingId, setSelectingId, dragItem, dragDisabled}) => {
  const dataUrl = useImageData(item.imageId);

  return (
    <Draggable position={{x: item.x, y: item.y}}
      onStart={() => setSelectingId(id)}
      onDrag={(ev: DraggableEvent, data: DraggableData) => dragItem(data, item, id)}
      scale={scaleValue} disabled={dragDisabled}>
      <UiPartsStaticImageDiv x={item.x} y={item.y}
        width={item.width} height={item.height}
        selecting={selectingId === id}>
        <StaticImageUiParts src={dataUrl} draggable="false"
          width={Math.abs(item.width)} height={item.height}
          radius={item.radius} />
      </UiPartsStaticImageDiv>
    </Draggable>
  );
}

type RenderItemsProps = {
  parentId: string;
  parent: 'card' | UiCardItemDb;
  items: EditUiCardItemDbHashType;
  changeItem: (item: UiCardItemDb, id?: string) => void;
  selectingId: string | undefined;
  setSelectingId: (id: string | undefined) => void;
  scaleValue: number;
  changeTab: () => void;
}
const RenderItems: React.FC<RenderItemsProps> = ({parentId, parent, items, changeItem, selectingId, setSelectingId, scaleValue, changeTab}) => {
  const ids = Object.keys(items).filter(id => {
    const item = items[id];
    return item && item.parentId === parentId;
  });
  ids.sort((a, b) => {
    const aValue = items[a] as UiCardItemDb;
    const bValue = items[b] as UiCardItemDb;
    return bValue.z - aValue.z;
  })

  const subIds = Object.keys(items).filter(id => {
    const item = items[id];
    return item && item.parentId !== parentId;
  });
  subIds.sort((a, b) => {
    const aValue = items[a] as UiCardItemDb;
    const bValue = items[b] as UiCardItemDb;
    return bValue.z - aValue.z;
  })

  type ChildrenHash = { [key in string]: EditUiCardItemDbHashType }
  const subLists: ChildrenHash = {};
  subIds.forEach(id => {
    const item = items[id];
    if (!item) return;
    if (!subLists[item.parentId]) subLists[item.parentId] = {};
    subLists[item.parentId][id] = item;
  })

  const handleDragStart = (id: string) => {
    setSelectingId(id);
  }
  const getFontData = (font: FontType): FontType => {
    let value = deepCopy(font);
    if (value.direction === 'vertical-rl') {
      type YokoType = 'left' | 'center' | 'right';
      type TateType = 'start' | 'center' | 'end';
      type YokoHashType = { [key in TateType]: YokoType };
      type TateHashType = { [key in YokoType]: TateType };
      const yokoHash: YokoHashType = {'start': 'left', 'center': 'center', 'end': 'right'};
      const tateHash: TateHashType = {'left': 'end', 'center': 'center', 'right': 'start'};
      const a = value.yoko;
      const b = value.tate;
      value.yoko = yokoHash[b];
      value.tate = tateHash[a];
    }
    return value;
  }
  const dragItem = (data: DraggableData, item: UiCardItemDb, id: string) => {
    const newAttrs = { x: data.x, y: data.y, width: item.width, height: item.height };
    const newItem = { ...item, ...newAttrs };
    changeItem(newItem, id);
  }

  return (
    <>
      { ids.map(id => {
        const item = items[id];
        if (!item) return undefined;
        if (item.type === 'input') {
          const { name, color, size, bold, italic } = getFontData(item.labelFont);
          return (
            <div onDoubleClick={() => changeTab()}>
              <Draggable position={{x: item.x, y: item.y}}
                onStart={() => handleDragStart(id)}
                onDrag={(ev: DraggableEvent, data: DraggableData) => dragItem(data, item, id)}
                scale={scaleValue} disabled={parent !== 'card' && !ids.includes(selectingId || '')}>
                <UiPartsInputDiv
                  x={item.x} y={item.y}
                  width={item.width} height={item.height}
                  selecting={selectingId === id}
                  labelVisibility={item.labelVisibility} size={size}
                >
                  { item.labelVisibility &&
                    <UiPartsLabel htmlFor={'input-' + id}
                      size={size} bold={bold} color={color} italic={italic} name={name}
                    >
                      {item.label}
                    </UiPartsLabel>
                  }
                  <UiPartsInputText type="text" id={'input-' + id}
                    value={item.placeholder}
                  />
                </UiPartsInputDiv>
              </Draggable>
              <div style={{display: selectingId === id ? undefined : 'none'}}>
                <ResizeBox id={id} item={item} scaleValue={scaleValue}
                  changeItem={changeItem} />
              </div>
            </div>
          );
        } else if (item.type === 'text') {
          const { name, color, size, bold, italic, underline, direction, orientation, yoko, tate } = getFontData(item.textFont);
          const labelFont = getFontData(item.labelFont);
          return (
            <div onDoubleClick={() => changeTab()}>
              <Draggable position={{x: item.x, y: item.y}}
                onStart={() => handleDragStart(id)}
                onDrag={(ev: DraggableEvent, data: DraggableData) => dragItem(data, item, id)}
                scale={scaleValue} disabled={parent !== 'card' && !ids.includes(selectingId || '')}>
                <UiPartsInputDiv
                  x={item.x} y={item.y}
                  width={item.width} height={item.height}
                  selecting={selectingId === id}
                  labelVisibility={item.labelVisibility} size={labelFont.size}
                >
                  { item.labelVisibility &&
                    <UiPartsLabel htmlFor={'text-' + id}
                      size={labelFont.size} bold={labelFont.bold} color={labelFont.color}
                      italic={false} underline={false} name={labelFont.name}
                      yoko='left' tate='center' direction='horizontal-tb' orientation='mixed'
                    >
                      { item.label }
                    </UiPartsLabel>
                  }
                  <TextDiv
                    flame={item.flame} flameColor={item.flameColor}
                    backgroundOpacity={item.backgroundOpacity} backgroundColor={item.backgroundColor}
                    size={size} bold={bold} color={color} italic={italic} underline={underline} name={name}
                    yoko={yoko} tate={tate} direction={direction} orientation={orientation}
                  >
                    { item.text }
                  </TextDiv>
                </UiPartsInputDiv>
              </Draggable>
              <div style={{display: selectingId === id ? undefined : 'none'}}>
                <ResizeBox id={id} item={item} scaleValue={scaleValue}
                  changeItem={changeItem} />
              </div>
            </div>
          );
        } else if (item.type === 'button') {
          const { name, color, size, bold, italic } = getFontData(item.labelFont);
          return (
            <div onDoubleClick={() => changeTab()}>
              <Draggable position={{x: item.x, y: item.y}}
                onStart={() => handleDragStart(id)}
                onDrag={(ev: DraggableEvent, data: DraggableData) => dragItem(data, item, id)}
                scale={scaleValue} disabled={parent !== 'card' && !ids.includes(selectingId || '')}>
                <UiPartsDiv
                  x={item.x} y={item.y}
                  width={item.width} height={item.height}
                  selecting={selectingId === id}
                >
                  <UiPartsButton backgroundColor={item.backgroundColor}
                    size={size} bold={bold} color={color} italic={italic} name={name}
                  >
                    {item.label}
                  </UiPartsButton>
                </UiPartsDiv>
              </Draggable>
              <div style={{display: selectingId === id ? undefined : 'none'}}>
                <ResizeBox id={id} item={item} scaleValue={scaleValue}
                  changeItem={changeItem} />
              </div>
            </div>
          );
        } else if (item.type === 'checkbox') {
          const { name, color, size, bold, italic } = getFontData(item.labelFont);
          return (
            <div onDoubleClick={() => changeTab()}>
              <Draggable position={{x: item.x, y: item.y}}
                onStart={() => handleDragStart(id)}
                onDrag={(ev: DraggableEvent, data: DraggableData) => dragItem(data, item, id)}
                scale={scaleValue} disabled={parent !== 'card' && !ids.includes(selectingId || '')}>
                <UiPartsCheckboxDiv
                  x={item.x} y={item.y}
                  width={item.width} height={item.height}
                  selecting={selectingId === id}
                >
                  <CheckboxUiParts id={'checkbox-' + id} size={item.height} />
                  <CheckboxCheckUiParts size={item.height} />
                  { item.labelVisibility &&
                    <UiPartsLabel htmlFor={'checkbox-' + id}
                      size={size} bold={bold} color={color} italic={italic} name={name}
                    >
                      {item.label}
                    </UiPartsLabel>
                  }
                </UiPartsCheckboxDiv>
              </Draggable>
              <div style={{display: selectingId === id ? undefined : 'none'}}>
                <ResizeBox id={id} item={item} scaleValue={scaleValue}
                  changeItem={changeItem} />
              </div>
            </div>
          );
        } else if (item.type === 'dropdown') {
          const { name, color, size, bold, italic } = getFontData(item.labelFont);
          const optionIds = Object.keys(item.options);
          return (
            <div onDoubleClick={() => changeTab()}>
              <Draggable position={{x: item.x, y: item.y}}
                onStart={() => handleDragStart(id)}
                onDrag={(ev: DraggableEvent, data: DraggableData) => dragItem(data, item, id)}
                scale={scaleValue} disabled={parent !== 'card' && !ids.includes(selectingId || '')}>
                <UiPartsInputDiv
                  x={item.x} y={item.y}
                  width={item.width} height={item.height}
                  selecting={selectingId === id}
                  labelVisibility={item.labelVisibility} size={size}
                >
                  { item.labelVisibility &&
                    <UiPartsLabel htmlFor={'dropdown-' + id}
                      size={size} bold={bold} color={color} italic={italic} name={name}
                    >
                      {item.label}
                    </UiPartsLabel>
                  }
                  <SelectUiParts id={'dropdown-' + id}>
                    { optionIds.map(id => <option value={id}>{item.options[id]}</option> ) }
                  </SelectUiParts>
                </UiPartsInputDiv>
              </Draggable>
              <div style={{display: selectingId === id ? undefined : 'none'}}>
                <ResizeBox id={id} item={item} scaleValue={scaleValue}
                  changeItem={changeItem} />
              </div>
            </div>
          );
        } else if (item.type === 'multi') {
          const { name, color, size, bold, italic } = getFontData(item.labelFont);
          return (
            <div onDoubleClick={() => changeTab()}>
              <Draggable position={{x: item.x, y: item.y}}
                onStart={() => handleDragStart(id)}
                onDrag={(ev: DraggableEvent, data: DraggableData) => dragItem(data, item, id)}
                scale={scaleValue} disabled={parent !== 'card' && !ids.includes(selectingId || '')}>
                <UiPartsInputDiv
                  x={item.x} y={item.y}
                  width={item.width} height={item.height}
                  selecting={selectingId === id}
                  labelVisibility={item.labelVisibility} size={size}
                >
                  { item.labelVisibility &&
                    <UiPartsLabel htmlFor={'multi-' + id}
                      size={size} bold={bold} color={color} italic={italic} name={name}
                    >
                      {item.label}
                    </UiPartsLabel>
                  }
                  <MultiUiParts id={'multi-' + id} rows={6}
                    value={item.placeholder}
                  />
                </UiPartsInputDiv>
              </Draggable>
              <div style={{display: selectingId === id ? undefined : 'none'}}>
                <ResizeBox id={id} item={item} scaleValue={scaleValue}
                  changeItem={changeItem} />
              </div>
            </div>
          );
        } else if (item.type === 'variable') {
          return (
            <div onDoubleClick={() => changeTab()}>
              <Draggable position={{x: item.x, y: item.y}}
                onStart={() => handleDragStart(id)}
                onDrag={(ev: DraggableEvent, data: DraggableData) => dragItem(data, item, id)}
                scale={scaleValue} disabled={parent !== 'card' && !ids.includes(selectingId || '')}>
                <UiPartsVariableImageDiv
                  x={item.x} y={item.y}
                  width={item.width} height={item.height}
                  selecting={selectingId === id}
                >
                  <VariableImageUiParts radius={item.radius}>
                    <UiPartsImageIcon size={Math.min(item.width, item.height)} />
                    {item.name}
                  </VariableImageUiParts>
                </UiPartsVariableImageDiv>
              </Draggable>
              <div style={{display: selectingId === id ? undefined : 'none'}}>
                <ResizeBox id={id} item={item} scaleValue={scaleValue}
                  changeItem={changeItem} />
              </div>
            </div>
          );
        } else if (item.type === 'static') {
          return (
            <div onDoubleClick={() => changeTab()}>
              <RenderImage item={item} scaleValue={scaleValue}
                id={id} selectingId={selectingId}
                setSelectingId={setSelectingId} dragItem={dragItem}
                dragDisabled={parent !== 'card' && !ids.includes(selectingId || '')} />
              <div style={{display: selectingId === id ? undefined : 'none'}}>
                <ResizeBox id={id} item={item} scaleValue={scaleValue}
                  changeItem={changeItem} />
              </div>
            </div>
          );
        } else if (item.type === 'group') {
          const subList = subLists[id];
          const childrenIds = subList ? Object.keys(subList) : [];
          return (
            <div onDoubleClick={() => changeTab()}>
              <Draggable position={{x: item.x, y: item.y}}
                onStart={() => handleDragStart(id)}
                onDrag={(ev: DraggableEvent, data: DraggableData) => dragItem(data, item, id)}
                scale={scaleValue} disabled={(parent !== 'card' && !ids.includes(selectingId || '')) || (selectingId !== undefined && childrenIds.includes(selectingId))}>
                <UiPartsDiv style={{overflow: 'hidden'}}
                  x={item.x} y={item.y}
                  width={item.width} height={item.height}
                  selecting={selectingId === id}
                >
                  <GroupDiv>
                    <GroupUiParts width={item.width - 8} height={item.height - 8}>
                    { subList &&
                      <RenderItems parentId={id} parent={item}
                        items={subList} changeItem={changeItem}
                        selectingId={selectingId} setSelectingId={setSelectingId}
                        scaleValue={scaleValue} 
                        changeTab={changeTab}
                      />
                    }
                    </GroupUiParts>
                  </GroupDiv>
                </UiPartsDiv>
              </Draggable>
              <div style={{display: selectingId === id ? undefined : 'none'}}>
                <ResizeBox id={id} item={item} scaleValue={scaleValue}
                  changeItem={changeItem} />
              </div>
            </div>
          );
        } else return undefined;
      }) }
    </>
  );
}

type RenderUiCardProps = {
  cardId: string;
  card: CardDb;
  items: EditUiCardItemDbHashType;
  changeItem: (item: UiCardItemDb, id?: string) => void;
  selectingId: string | undefined;
  setSelectingId: (id: string | undefined) => void;
  scaleValue: number;
  workHeight: number;
  changeTab: () => void;
}
const RenderUiCard: React.FC<RenderUiCardProps> = ({cardId, card, items, changeItem, selectingId, setSelectingId, scaleValue, workHeight, changeTab}) => {
  const width = card.orientation === 'vertical' ? initial.uiSketchNarrow : initial.uiSketchWide;
  const height = card.orientation === 'vertical' ? initial.uiSketchWide : initial.uiSketchNarrow;

  return (
    <Box style={{height: workHeight}}>
      <CardPaper id='base' width={width} height={height}
        elevation={card.shadow ? 8 : 0}
        frameDisplay={card.frame.display} frameColor={card.frame.color}
        scaleValue={scaleValue}>
        <RenderItems parentId={cardId} parent="card"
          items={items} changeItem={changeItem}
          selectingId={selectingId} setSelectingId={setSelectingId}
          scaleValue={scaleValue} 
          changeTab={changeTab}
        />
      </CardPaper>
    </Box>
  );
}

export default RenderUiCard;
